import type { VoiceAgent, PhoneNumber, CallLog } from "./voice.types";
import type {
  DfVoiceAgent,
  DfPhoneNumber,
  DfCallHistory,
} from "../../services/voiceDataFabricService";

export const AGENT_COLORS = [
  "linear-gradient(135deg,#8b5cf6,#6d28d9)",
  "linear-gradient(135deg,#6366f1,#4f46e5)",
  "linear-gradient(135deg,#22d3ee,#0891b2)",
  "linear-gradient(135deg,#14b8a6,#0d9488)",
  "linear-gradient(135deg,#ec4899,#db2777)",
  "linear-gradient(135deg,#f59e0b,#d97706)",
];

export function formatDuration(
  seconds: number | string | null | undefined,
): string {
  if (!seconds && seconds !== 0) return "—";
  const s = typeof seconds === "string" ? parseInt(seconds, 10) : seconds;
  if (isNaN(s) || s <= 0) return "—";
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
}

function resolveDirection(raw: string): "in" | "out" | "missed" {
  const d = raw.toLowerCase();
  if (d.includes("inbound")) return "in";
  if (d.includes("missed") || d === "no-answer" || d === "canceled")
    return "missed";
  return "out";
}

/* ── DataFabric mappers (camelCase shapes → UI types) ─────────────────────────
   Source: DataFabric /api/IGIGMomentumVoice/* (see voiceDataFabricService.ts). */

export function mapDfAgent(a: DfVoiceAgent, idx: number): VoiceAgent {
  const name = a.agentName ?? "?";
  return {
    id: a.id ?? idx,
    elevenLabsAgentId: a.elevenLabsAgentId ?? "",
    name,
    agentType: "outbound",
    voiceId: a.voiceSelection ?? "",
    welcomeMessage: a.welcomeMessage ?? "",
    systemMessage: a.systemMessage ?? "",
    voicemailMessage: a.voiceMailMessage ?? "",
    createdAt: a.created ?? "",
    status: "online",
    color: AGENT_COLORS[idx % AGENT_COLORS.length],
    initials: name
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .join("")
      .slice(0, 2)
      .toUpperCase(),
  };
}

export function mapDfPhone(p: DfPhoneNumber): PhoneNumber {
  return {
    id: p.id ?? 0,
    phoneNumberId: p.phoneNumberId ?? "",
    // Twilio SID used for the release endpoint — try twilioSid, sid, then fall back to phoneNumberId
    twilioSid: p.twilioSid ?? p.sid ?? p.phoneNumberId ?? "",
    phoneNumber: p.phoneNumber ?? "",
    label: p.label ?? "",
    provider: p.provider ?? "",
    supportsInbound: !!p.supportsInbound,
    supportsOutbound: !!p.supportsOutbound,
    assignedAgentId: p.assignedAgentId ?? null,
    assignedAgentName: p.assignedAgentName ?? null,
    createdAt: p.created ?? "",
  };
}

/** Maps a DataFabric call. `agentNames` resolves agentId → agent display name. */
export function mapDfCall(
  c: DfCallHistory,
  idx: number,
  agentNames: Record<string, string> = {},
): CallLog {
  const direction = resolveDirection(c.direction ?? "");
  const contactName =
    c.firstName || c.lastName
      ? [c.firstName, c.lastName].filter(Boolean).join(" ")
      : "";
  // Inbound: external caller → agent number; Outbound: agent number → external.
  const from =
    direction === "in"
      ? (c.externalNumber ?? c.phoneNumber ?? "—")
      : (c.agentNumber ?? "—");
  const to =
    direction === "in"
      ? (c.agentNumber ?? "—")
      : (c.phoneNumber ?? c.externalNumber ?? "—");
  const timestamp = c.created
    ? new Date(c.created).toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "—";
  return {
    id: String(c.id ?? idx),
    callSid: c.callSid ?? null,
    agentId: c.agentId ?? null,
    contactName,
    direction,
    from,
    to,
    agent: (c.agentId && agentNames[c.agentId]) || "—",
    duration: formatDuration(c.callDurationSecs),
    timestamp,
    status: (c.callStatus ?? "unknown").toLowerCase(),
  };
}
